<template>
  <v-container fluid class="painel-dashboard">

    <BrAppBar />

    <!-- Page header -->
    <div class="painel-header">
      <div>
        <h1 class="painel-title">{{ $t('painel.title') }}</h1>
        <p class="painel-subtitle">{{ $t('painel.subtitle') }}</p>
      </div>
      <FiltroRangeDate
        :value="rangeDate"
        @input="onDateChange"
        :labelBtn="$t('common.period')"
        :actionLabels="{ apply: $t('common.ok') }"
        :btnProps="{ outlined: true }"
      />
    </div>

    <!-- Visitas chart -->
    <Card :title="$t('painel.visitas')" class="mb-4">
      <v-skeleton-loader v-if="loading" type="image" height="220" class="painel-skeleton" />
      <ChartLine v-else :series="visitasSeries" :categories="visitasCategories" />
    </Card>

    <!-- KPI cards -->
    <v-row class="mb-4">
      <v-col
        v-for="card in kpiCards"
        :key="card.label"
        cols="12"
        sm="6"
        md="3"
      >
        <KpiCard :label="card.label" :value="card.value" :badge="card.badge" :loading="loading" />
      </v-col>
    </v-row>

    <!-- Ajustes -->
    <Card :title="$t('painel.ajustes.title')" class="mb-4">
      <template v-if="loading">
        <v-skeleton-loader
          v-for="n in 3"
          :key="n"
          type="text"
          class="painel-skeleton mb-2"
          height="20"
        />
      </template>
      <template v-else>
        <div v-if="!adjustments.length" class="ajustes-empty">
          {{ $t('painel.ajustes.empty') }}
        </div>
        <div v-else class="ajustes-list">
          <div class="ajustes-row ajustes-row--header">
            <span>{{ $t('painel.ajustes.data') }}</span>
            <span>{{ $t('painel.ajustes.valor') }}</span>
          </div>
          <div
            v-for="(item, idx) in adjustments"
            :key="idx"
            class="ajustes-row"
            :class="{ 'ajustes-row--border': idx < adjustments.length - 1 }"
          >
            <span class="ajustes-date">{{ item.date }}</span>
            <span
              class="ajustes-value"
              :style="{ color: item.value > 0 ? variables.colorSuccess : variables.colorError }"
            >
              {{ item.value > 0 ? '+' : '-' }}{{ Math.abs(item.value) | currency }}
            </span>
          </div>
        </div>
      </template>
    </Card>

    <!-- QFTDs/CPA + Comissão Sub Afiliados -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <Card :title="$t('painel.qftdsCpa')">
          <v-skeleton-loader v-if="loading" type="image" height="220" class="painel-skeleton" />
          <ChartLine v-else :series="qftdCpaSeries" :categories="qftdCpaCategories" :colors="[variables.colorSuccess, variables.colorInfo]" />
        </Card>
      </v-col>
      <v-col cols="12" md="6">
        <Card :title="$t('painel.subAfiliados')">
          <v-skeleton-loader v-if="loading" type="image" height="220" class="painel-skeleton" />
          <ChartLine v-else :series="subAffiliatesSeries" :categories="subAffiliatesCategories" />
        </Card>
      </v-col>
    </v-row>

    <!-- RevShare chart -->
    <Card :title="$t('painel.revShare')" class="mb-4">
      <v-skeleton-loader v-if="loading" type="image" height="220" class="painel-skeleton" />
      <ChartLine v-else :series="revShareSeries" :categories="revShareCategories" />
    </Card>

  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { currency } from '@/core/service/utils'
import variables from '@/assets/styles/helpers/_variables.scss'

export default {
  name: 'painel',
  components: {
    BrAppBar: () => import('@/views/layout/appBar'),
    FiltroRangeDate: () => import('@/views/components/filtroRangeDate'),
    ChartLine: () => import('./components/ChartLine.vue'),
    Card: () => import('@/views/components/card'),
    KpiCard: () => import('@/views/components/kpiCard'),
  },
  data () {
    return {
      rangeDate: {
        start: moment().startOf('month').format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
      },
      loading: false,
    }
  },
  computed: {
    ...mapGetters('painel', [
      'listaItens',
      'listaItensCards',
      'visitasSeries',
      'visitasCategories',
      'qftdCpaSeries',
      'qftdCpaCategories',
      'subAffiliatesSeries',
      'subAffiliatesCategories',
      'revShareSeries',
      'revShareCategories',
      'adjustments',
      'listaItensVariation',
    ]),
    variables: () => variables,
    kpiCards () {
      return [
        { label: this.$t('painel.kpi.comissoes'), value: currency(this.listaItens?.recNGR  || 0), badge: this.variationBadge('recNGR') },
        { label: this.$t('painel.kpi.registros'), value: this.listaItensCards?.qty_clients  || 0 },
        { label: this.$t('painel.kpi.ftds'),      value: this.listaItens?.ftd               || 0, badge: this.variationBadge('ftd') },
        { label: this.$t('painel.kpi.cpa'),       value: currency(this.listaItens?.cpa      || 0), badge: this.variationBadge('cpa') },
      ]
    },
  },
  methods: {
    ...mapActions('painel', ['getDashBoard', 'getDadosVisitas']),
    badgeStyleFor (color) {
      const hex = color.replace('#', '')
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)
      return {
        background: `rgba(${r}, ${g}, ${b}, 0.18)`,
        color,
      }
    },
    variationBadge (key) {
      const variation = this.listaItensVariation ? this.listaItensVariation[key] : undefined
      if (variation === undefined) return null

      if (variation === null) {
        return { style: this.badgeStyleFor(variables.colorAccent), icon: null, text: this.$t('painel.kpi.novo') }
      }
      if (variation > 0) {
        return { style: this.badgeStyleFor(variables.colorSuccess), icon: 'mdi-trending-up', text: `+${variation}%` }
      }
      if (variation < 0) {
        return { style: this.badgeStyleFor(variables.colorError), icon: 'mdi-trending-down', text: `${variation}%` }
      }
      return { style: this.badgeStyleFor(variables.colorSuccess), icon: 'mdi-trending-up', text: '0%' }
    },
    onDateChange ({ start, end }) {
      this.rangeDate = { start, end }
      this.fetchAll()
    },
    async fetchAll () {
      this.loading = true
      try {
        await this.getDashBoard({ dtIni: this.rangeDate.start, dtEnd: this.rangeDate.end })
        await this.getDadosVisitas({ dtIni: this.rangeDate.start, dtEnd: this.rangeDate.end })
      } finally {
        this.loading = false
      }
    },
  },
  mounted () {
    this.fetchAll()
  },
}
</script>

<style lang="scss" scoped>
$label: #C7C7D8;
$muted: #9B9BAA;

.painel-dashboard {
  padding: 16px 24px 24px;
}

.painel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;

  .painel-title {
    font-size: 24px;
    font-weight: 700;
    color: #fff;
    margin: 0;
  }

  .painel-subtitle {
    font-size: 13px;
    color: $label;
    margin: 4px 0 0;
  }
}

.ajustes-empty {
  font-size: 13px;
  color: $muted;
  padding: 12px 0;
}

.ajustes-list {
  display: flex;
  flex-direction: column;
}

.ajustes-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;

  &--header {
    font-size: 12px;
    font-weight: 600;
    color: $label;
    text-transform: uppercase;
    padding-bottom: 8px;
  }

  &--border {
    border-bottom: 1px solid rgba(124, 124, 138, 0.2);
  }
}

.ajustes-date {
  font-size: 13px;
  color: #fff;
}

.ajustes-value {
  font-size: 13px;
  font-weight: 600;
}

// Skeleton overrides — dark surface (#1D1D29 cards)
.painel-skeleton {
  border-radius: 6px;
  overflow: hidden;

  ::v-deep .v-skeleton-loader__image,
  ::v-deep .v-skeleton-loader__text,
  ::v-deep .v-skeleton-loader__chip,
  ::v-deep .v-skeleton-loader__heading,
  ::v-deep .v-skeleton-loader__bone {
    background: rgba(255, 255, 255, 0.08) !important;
    margin: 0 !important;
  }

  ::v-deep .v-skeleton-loader__bone::after {
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.06),
      transparent
    ) !important;
  }
}
</style>
